import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AWAAssetPage } from './awa-pages/[workspaceId]/assets/[id]';
import { AWAInspectionPage } from './awa-pages/[workspaceId]/inspection';
import { AWAIssuesPage } from './awa-pages/[workspaceId]/issues';
import { AWAIssuesAssetPage } from './awa-pages/[workspaceId]/issues/assets/[id]';
import { AWAMaintenancePage } from './awa-pages/[workspaceId]/maintenance';
import { AWARepairPage } from './awa-pages/[workspaceId]/repair';
import { AWASchedulePage } from './awa-pages/[workspaceId]/schedule';
import { AWAScheduleChecksPage } from './awa-pages/[workspaceId]/schedule-checks';
import { AWAScheduleChecksAssetPage } from './awa-pages/[workspaceId]/schedule-checks/assets/[id]';
import AWAWorkOrdersPage from './awa-pages/[workspaceId]/workorders';
import { AWAAuthEmailSetupPage } from './awa-pages/auth/email-setup';
import { AWAAuthLoginPage } from './awa-pages/auth/login';
import { AWAAuthOnboardingPage } from './awa-pages/auth/onboarding';
import { AWAFeatureFlagsPage } from './awa-pages/featureflags';
import AWANotFoundPage from './awa-pages/not-found';
import AWAPreStartRedirect from './awa-pages/prestart-redirect';
import { AWAQRPage } from './awa-pages/qr/[qrId]';
import { AWAAssignQRPage } from './awa-pages/qr/[qrId]/assign';
import AWARootRedirect from './awa-pages/root-redirect';
import AWAWorkspaceRedirect from './awa-pages/workspace-redirect';
import { FormDesigner } from './components/form/form-designer';
import AWALayout from './components/layouts/awa-layout';
import AWALayoutV2 from './components/layouts/awa-layout-v2';
import DefaultLayout from './components/layouts/default-layout';
import { SiteBanners } from './components/site-banner/site-banners';
import { Toaster } from './components/ui/toaster';
import AWARoute from './guards/awa-route';
import CreateWorkspaceRoute from './guards/create-workspace-route';
import GuestRoute from './guards/guest-route';
import ProtectedRoute from './guards/protected-route';
import WorkOrderGbmwRedirectRoute from './guards/work-order-gbmw-redirect-route';
import WorkspaceRoute from './guards/workspace-route';
import { ActivateAccountPage } from './pages/activate-account';
import AssetsPage from './pages/assets';
import AssetPage from './pages/assets/[id]';
import AssetDocumentsPage from './pages/assets/[id]/documents';
import AssetInspectionsPage from './pages/assets/[id]/inspections';
import AssetIssuesPage from './pages/assets/[id]/issues';
import AssetLocationLogsPage from './pages/assets/[id]/location-logs';
import { AssetOverviewPage } from './pages/assets/[id]/overview';
import AssetReadingLogsPage from './pages/assets/[id]/reading-logs';
import AssetRepairsPage from './pages/assets/[id]/repairs';
import AssetScheduleChecksPage from './pages/assets/[id]/schedule-checks';
import AssetScheduleRecordsPage from './pages/assets/[id]/schedule-records';
import AssetWorkOrdersPage from './pages/assets/[id]/work-orders';
import { CreateWorkspacePage } from './pages/create-workspace';
import { DashboardPage } from './pages/dashboard';
import { DeploymentsPage } from './pages/deployment';
import DeploymentPage from './pages/deployment/[id]';
import DeploymentCreatePage from './pages/deployment/create';
import CompliancePage from './pages/documents';
import { ExportProgressPage } from './pages/export';
import IMSPage from './pages/ims';
import InspectionActivityPage from './pages/inspections/activity';
import InspectionFormsPage from './pages/inspections/forms';
import InspectionFormSubmissionsPage from './pages/inspections/forms/[id]/submissions/index';
import IssuesPage from './pages/issues';
import RepairActivityPage from './pages/issues/repairs/activity';
import RepairFormsPage from './pages/issues/repairs/forms';
import RepairSubmissionsPage from './pages/issues/repairs/forms/[id]/submissions';
import ListsPage from './pages/lists';
import LocationsPage from './pages/locations';
import LocationPage from './pages/locations/[id]';
import LocationsLayout from './pages/locations/layout';
import LocationLogsPage from './pages/locations/location-logs';
import LoginPage from './pages/login';
import LogoutPage from './pages/logout';
import NoWorkspaceAvailable from './pages/no-workspace-available';
import NotFoundPage from './pages/not-found';
import { OnboardPage } from './pages/onboard';
import ProfilePage from './pages/profile';
import Redirect from './pages/redirect';
import SchedulesPage from './pages/schedules';
import ScheduleEditorPage from './pages/schedules/[id]';
import ScheduleActivityPage from './pages/schedules/activity';
import ScheduleChecksPage from './pages/schedules/checks';
import ScheduleFormFormsPage from './pages/schedules/forms';
import ScheduleFormFormSubmissionsPage from './pages/schedules/forms/[id]';
import SettingsPage from './pages/settings';
import SettingsSitemateStartGearbeltBillingPage from './pages/settings/sitemate-start/gearbelt-billing';
import SettingsSitemateStartGeneralPage from './pages/settings/sitemate-start/general';
import SettingsSitemateStartGlobalBillingPage from './pages/settings/sitemate-start/global-billing';
import SettingsSitemateStartSecurityPage from './pages/settings/sitemate-start/security';
import SettingsBrandingPage from './pages/settings/workspace/branding';
import SettingsDocumentsPage from './pages/settings/workspace/documents';
import SettingsFeatureAccessPage from './pages/settings/workspace/feature-access';
import SettingsGeneralPage from './pages/settings/workspace/general';
import SettingsIssuesPage from './pages/settings/workspace/issues';
import SettingsNotificationsPage from './pages/settings/workspace/notifications';
import SettingsSchedulesPage from './pages/settings/workspace/schedules';
import SettingsWorkOrdersPage from './pages/settings/workspace/work-orders';
import { ReCAPTCHAWrappedSignUpPage } from './pages/signup';
import UsersPage from './pages/users';
import WorkOrdersPage from './pages/work-orders';
import WorkOrderRedirectPage from './pages/work-orders/[id]';
import WorkspaceRedirect from './pages/workspace-redirect';
import { WorkspaceSetupPage } from './pages/workspace-setup';
import { useIsAWA } from './utils/awa';
import { getWorkspaceRoute } from './utils/routes';

function BuildRouteRedirectionLink({ newRoute }: { newRoute: string }) {
  const location = useLocation();
  return <Navigate to={`/${newRoute}${location.search}`} replace />;
}

const GearbeltApp = () => {
  const { isAWA } = useIsAWA();

  return (
    <div className="flex h-[100dvh] flex-col">
      <SiteBanners />
      <Routes>
        {/* AWA ROUTES START */}
        {(() => {
          // Only render if we're on the forms hostname
          return (
            isAWA && (
              <>
                <Route element={<AWARoute />}>
                  <Route element={<AWALayoutV2 />}>
                    <Route path=":workspaceId/assets/:assetId" element={<AWAAssetPage />} />
                    <Route path=":workspaceId/schedule-checks" element={<AWAScheduleChecksPage />} />
                    <Route path=":workspaceId/schedule-checks/:assetId" element={<AWAScheduleChecksAssetPage />} />
                    <Route path=":workspaceId/issues" element={<AWAIssuesPage />} />
                    <Route path=":workspaceId/issues/:assetId" element={<AWAIssuesAssetPage />} />
                  </Route>
                  <Route element={<AWALayout />}>
                    <Route path=":workspaceId/maintenance" element={<AWAMaintenancePage />} />

                    <Route path=":workspaceId/workorders" element={<AWAWorkOrdersPage />} />
                    <Route path=":workspaceId/inspection/:assetId" element={<AWAInspectionPage />} />
                    <Route path=":workspaceId/inspection" element={<AWAInspectionPage />} />
                    <Route path=":workspaceId/schedule/:assetId" element={<AWASchedulePage />} />
                    <Route path=":workspaceId/schedule" element={<AWASchedulePage />} />
                    <Route path=":workspaceId/repair/:assetId" element={<AWARepairPage />} />
                    <Route path=":workspaceId/repair" element={<AWARepairPage />} />
                  </Route>

                  <Route path="auth/onboarding" element={<AWAAuthOnboardingPage />} />

                  <Route path=":workspaceId/prestart/:assetId" element={<AWAPreStartRedirect />} />
                  <Route path=":workspaceId/prestart" element={<AWAPreStartRedirect />} />
                  <Route path=":workspaceId" element={<AWAWorkspaceRedirect />} />
                  <Route path=":workspaceId/qr/:qrId/assign" element={<AWAAssignQRPage />} />

                  <Route path="" element={<AWARootRedirect />} />
                  <Route path="*" element={<AWARootRedirect />} />
                </Route>

                <Route path="auth/login" element={<AWAAuthLoginPage />} />
                <Route path="auth/email-setup" element={<AWAAuthEmailSetupPage />} />
                <Route path="qr/:qrId" element={<AWAQRPage />} />
                <Route path="featureflags" element={<AWAFeatureFlagsPage />} />
                <Route path="404" element={<AWANotFoundPage />} />
              </>
            )
          );
        })()}
        {/* AWA ROUTES END */}

        {!isAWA && (
          <>
            <Route path="/redirect" element={<Redirect />} />
            <Route path="/ims/*" element={<IMSPage />} />
            <Route path="/no-workspace-available" element={<NoWorkspaceAvailable />} />

            {/* Runs before ProtectedRoute so a mobile/tablet device is bounced to GBMW
                without first requiring a Gearbelt Web login — see work-order-gbmw-redirect-route.tsx. */}
            <Route path="/workspaces/:workspaceId/work-orders/:id" element={<WorkOrderGbmwRedirectRoute />}>
              <Route element={<ProtectedRoute />}>
                <Route element={<DefaultLayout />}>
                  <Route element={<WorkspaceRoute />}>
                    <Route index element={<WorkOrderRedirectPage />} />
                  </Route>
                </Route>
              </Route>
            </Route>

            <Route path="*" element={<Redirect />} />

            {/* CONSOLE ROUTES START */}
            <Route element={<ProtectedRoute />}>
              <Route path="/workspace-setup" element={<WorkspaceSetupPage />} />
              <Route path="/404" element={<NotFoundPage />} />
              <Route element={<CreateWorkspaceRoute />}>
                <Route path="/create-workspace" element={<CreateWorkspacePage />} />
              </Route>

              {/* Full-screen export progress tab — authenticated + workspace-scoped, but
                  deliberately outside DefaultLayout so it renders without the app chrome. */}
              <Route element={<WorkspaceRoute />}>
                <Route path="/exports/progress" element={<ExportProgressPage />} />
              </Route>

              <Route element={<DefaultLayout />}>
                <Route path="/" element={<WorkspaceRedirect />} />
                <Route element={<WorkspaceRoute />}>
                  <Route path="workspaces/:workspaceId">
                    <Route path="" element={<Navigate to={getWorkspaceRoute('/dashboard')} replace />} />
                    <Route path="dashboard" element={<DashboardPage />} />
                    <Route path="assets/:id" element={<AssetPage />}>
                      <Route path="" element={<AssetOverviewPage />} />
                      <Route path="documents" element={<AssetDocumentsPage />} />
                      <Route path="inspections" element={<AssetInspectionsPage />} />
                      <Route path="issues" element={<AssetIssuesPage />} />
                      <Route path="repairs" element={<AssetRepairsPage />} />
                      <Route path="schedule-checks" element={<AssetScheduleChecksPage />} />
                      <Route path="schedule-form-records" element={<AssetScheduleRecordsPage />} />
                      <Route path="work-orders" element={<AssetWorkOrdersPage />} />
                      <Route path="location-logs" element={<AssetLocationLogsPage />} />
                      <Route path="reading-logs" element={<AssetReadingLogsPage />} />
                    </Route>
                    <Route path="assets" element={<AssetsPage />} />
                    <Route path="users" element={<UsersPage />} />
                    <Route path="locations" element={<LocationsLayout />}>
                      <Route path="" element={<LocationsPage />} />
                      <Route path="location-logs" element={<LocationLogsPage />} />
                    </Route>
                    <Route path="lists" element={<ListsPage />} />
                    <Route path="forms/create" element={<FormDesigner />} />
                    <Route path="forms/:formId" element={<FormDesigner />} />
                    <Route path="locations/:id/:view" element={<LocationPage />} />
                    <Route path="inspections/activity" element={<InspectionActivityPage />} />
                    <Route path="inspections/forms" element={<InspectionFormsPage />} />
                    <Route path="inspections/forms/:id/submissions" element={<InspectionFormSubmissionsPage />} />
                    <Route path="issues/repairs/activity" element={<RepairActivityPage />} />
                    <Route path="issues" element={<IssuesPage />} />
                    <Route path="issues/repairs/forms" element={<RepairFormsPage />} />
                    <Route path="issues/repairs/forms/:id/submissions" element={<RepairSubmissionsPage />} />
                    <Route path="schedules/activity" element={<ScheduleActivityPage />} />
                    <Route path="schedules" element={<SchedulesPage />} />
                    <Route path="schedules/new" element={<ScheduleEditorPage />} />
                    <Route path="schedules/checks" element={<ScheduleChecksPage />} />
                    <Route path="schedules/upcoming-checks" element={<Navigate to={getWorkspaceRoute('/schedules/checks')} replace />} />
                    <Route path="schedules/:id" element={<ScheduleEditorPage />} />
                    <Route path="schedules/forms" element={<ScheduleFormFormsPage />} />
                    <Route path="schedules/forms/:id/submissions" element={<ScheduleFormFormSubmissionsPage />} />
                    <Route path="deployments" element={<DeploymentsPage />} />
                    <Route path="deployments/create" element={<DeploymentCreatePage />} />
                    <Route path="deployments/:id" element={<DeploymentPage />} />
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="reporting/documents" element={<CompliancePage />} />
                    <Route path="work-orders" element={<WorkOrdersPage />} />
                    <Route path="settings" element={<SettingsPage />}>
                      <Route path="" element={<Navigate replace to={getWorkspaceRoute('/settings/workspace/general')} />} />
                      <Route path="workspace/general" element={<SettingsGeneralPage />} />
                      <Route path="workspace/branding" element={<SettingsBrandingPage />} />
                      <Route path="workspace/feature-access" element={<SettingsFeatureAccessPage />} />
                      <Route path="workspace/schedules" element={<SettingsSchedulesPage />} />
                      <Route path="workspace/notifications" element={<SettingsNotificationsPage />} />
                      <Route path="workspace/issues" element={<SettingsIssuesPage />} />
                      <Route path="workspace/documents" element={<SettingsDocumentsPage />} />
                      <Route path="workspace/work-orders" element={<SettingsWorkOrdersPage />} />
                      <Route path="sitemate-start/general" element={<SettingsSitemateStartGeneralPage />} />
                      <Route path="sitemate-start/gearbelt-billing" element={<SettingsSitemateStartGearbeltBillingPage />} />
                      <Route path="sitemate-start/billing" element={<SettingsSitemateStartGlobalBillingPage />} />
                      <Route path="sitemate-start/security" element={<SettingsSitemateStartSecurityPage />} />
                    </Route>
                    {/* Catch-all for unknown paths under /workspaces/:workspaceId/* — redirect to
                        this workspace's dashboard so typos (e.g. a relative `workspace-setup` typed
                        while on `/workspaces/:id/dashboard`) don't land on a blank screen. */}
                    <Route path="*" element={<Navigate to="dashboard" replace />} />
                  </Route>
                </Route>
              </Route>
            </Route>

            <Route element={<GuestRoute />}>
              <Route path="/signup" element={<ReCAPTCHAWrappedSignUpPage />} />
              <Route path="/login" element={<LoginPage />} />
              {/* No GBMW device redirect here: an invitee has no account until these
                  pages complete, and GBMW has no onboarding surface — phones are routed
                  to GBMW post-auth by AppBootstrap instead. */}
              <Route path="/activate" element={<ActivateAccountPage />} />
              <Route path="/onboard" element={<OnboardPage />} />
            </Route>

            <Route element={<GuestRoute />}>
              <Route path="/register" element={<BuildRouteRedirectionLink newRoute="signup" />} />
              <Route path="/accept" element={<BuildRouteRedirectionLink newRoute="activate" />} />
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route path="/org-setup" element={<Navigate to="/workspace-setup" replace />} />
              <Route path="/logout" element={<LogoutPage />} />
              <Route path="/" element={<Navigate to={getWorkspaceRoute('/dashboard')} replace />} />
              <Route path="/dashboard" element={<Navigate to={getWorkspaceRoute('/dashboard')} replace />} />
            </Route>
            {/* CONSOLE ROUTES END */}
          </>
        )}
      </Routes>

      <Toaster />
    </div>
  );
};

export default GearbeltApp;
